/**
 * SettlementProxyModule — the FAR TIER (settlement study R7 v3, 2026-09-02).
 *
 * Two representations for every settlement whose real instanced set is not
 * built (too far, or still streaming in):
 *   HLOD-0 — within `r0` (the tier draw distance, ≥ 1.5 km): the REAL
 *            simplified prefabs (LOD1 from the packs, LOD2 when the tier is
 *            low) instanced at the resolved placements — real silhouettes,
 *            real textures, one batch per (geometry, material).
 *   HLOD-1 — out to the ladder's BOUNDED box reach (2× the draw distance,
 *            4–12 km, nothing beyond): six-band silhouette ladders cut from
 *            each prefab's own vertex cloud, shaded base → crown, one
 *            instanced draw with a faint night emissive. A site the real
 *            ring shows drops out of the box set.
 * Both are anchored to the render origin (instanced precision law), seated on
 * the baked pad heights, and PIVOT-COMPENSATED exactly like the assembly (the
 * cooked civ GLBs are off-pivot by up to 1.3 km — `seatPlacement`). Hidden the tick a site's real set is
 * built, shown again when it disposes behind the player, so the skyline also
 * covers the stream-in gap of a city you are entering.
 *
 * Diag: `__proxies()`. Opt out: localStorage `atlas:proxies` = 'off'.
 */
import type { App, IModule } from 'enginex';
import * as THREE from 'three/webgpu';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { activePreset, currentDrawDistance, distantDetailOn } from '../lib/quality_tiers.js';
import { settlementBands, effectiveDistance, closingSpeed, hlodLodFor, type SettlementBands } from '../lib/settlement_lod.js';
import { playerVelocity } from '../lib/player_motion.js';
import type { SettlementTier } from '../../../shared/settlements.js';
import { stampedHeight } from '../lib/terrain_stamp.js';
import { makeAnchorGroup, toAnchorLocal, onRenderAnchor } from '../lib/local_origin.js';
import { StaticInstancer } from '../lib/instancing.js';
import { proxyFileFor, proxyFileProblem, proxyVisible, seatPlacement, setProxyKeys, ladderSolids, BOX_STRIDE, STRUCT_STRIDE, type ProxyFile, type ProxySite } from '../lib/settlement_proxy.js';
import { addStructureSolid, clearStructureSolids } from '../lib/structure_solids.js';
import { setStructureMasksSeed, structureMasksDiag } from '../lib/structure_masks.js';
import { uNightFactor } from './SkyModule.js';
import { cityLightsCarryFarTier } from './CityNightLightsModule.js';
import type { CommonsSettlementsModule } from './CommonsSettlementsModule.js';
import type { AssetModule } from './AssetModule.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';

const TICK_EVERY = 12;
const BOX_CAPACITY = 65536;
/** The solids registry id an HLOD-0 site's band solids live under — disjoint from the real build's
 *  `site.id` (≥ 0) and the self-tests' −998/−999, so the two never clear each other. */
export function hlod0SolidSite(siteId: number): number { return -(1000 + siteId); }
/** The assembly's gate: a structure under this tall (scaled) is pass-through (props, sheds). */
const SOLID_MIN_H = 4;

interface Part { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }
interface Master { object: THREE.Object3D; release: () => void; parts: Part[]; /** natural-scale bounds — the assembly's `bboxFor` (setFromObject) */ bb: { minX: number; maxX: number; minY: number; minZ: number; maxZ: number } }
interface Hlod0Site { site: ProxySite; lod: 2 | 3; handles: number[]; placing: boolean; /** the previous level's instances, removed once this level has landed (no gap at a band swap) */ prev: number[]; /** band solids registered under hlod0SolidSite(site.id) */ solids: number }
interface PlacementAudit { maxSpread: number; maxSpreadSite: number; maxSpreadKey: string; seatErrMin: number; seatErrMax: number; audited: number }

export class SettlementProxyModule implements IModule {
  readonly name = 'SettlementProxyModule';
  private app: App | null = null;
  private scene: THREE.Scene | null = null;
  private file: ProxyFile | null = null;
  private fileSeed = '';
  private loading = false;
  private frame = 0;
  // HLOD-1
  private boxMesh: THREE.InstancedMesh | null = null;
  private boxMat: THREE.MeshStandardMaterial | null = null;
  private anchor: { group: THREE.Group; release: () => void } | null = null;
  private absMatrices: THREE.Matrix4[] = [];
  private unsubAnchor: (() => void) | null = null;
  private boxKey = '';
  // HLOD-0
  private instancer: StaticInstancer | null = null;
  private readonly masters = new Map<string, Promise<Master | null>>();
  private readonly hlod0 = new Map<number, Hlod0Site>();
  private readonly shown1 = new Set<number>();
  readonly stats = { fileSites: 0, boxSites: 0, boxes: 0, hlod0Sites: 0, hlod0Instances: 0, hlod0Solids: 0, r0: 0, hlod2: 0, lod2Sites: 0, lod3Sites: 0, masters: 0, rebuilds: 0, swaps: 0 };
  /** Placement audit (probe): the farthest any HLOD-0 instance's rendered bbox centre sits from its site centre, and the seat error range (bbox bottom − baked ground). */
  readonly audit: PlacementAudit = { maxSpread: 0, maxSpreadSite: -1, maxSpreadKey: '', seatErrMin: 0, seatErrMax: 0, audited: 0 };

  constructor(private readonly settlements: CommonsSettlementsModule, private readonly assets: AssetModule) {}

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    app.world.addSystem(makeSystem('SettlementProxies', Phase.RenderSub, 39, () => this.tick()));
    bus.on('atlas:world-entered', () => { this.boxKey = ''; void this.ensureFile(); });
    // Probe/diag: the ground-level wall masks (sites decoded, bytes, the walls-hit counter).
    if (typeof window !== 'undefined') (window as unknown as Record<string, unknown>).__structureMasks = () => structureMasksDiag();
  }

  private static optedOut(): boolean {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem('atlas:proxies') === 'off'; } catch { return false; }
  }

  private commonsSeed(): string | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return null;
  }

  private async ensureFile(): Promise<void> {
    const seed = this.commonsSeed();
    if (!seed || this.loading || (this.file && this.fileSeed === seed)) return;
    this.loading = true;
    // The walking-height masks ride the same seed (their own fetch; a 404 is "no walls").
    setStructureMasksSeed(seed);
    try {
      const res = await fetch(`/proxies/${proxyFileFor(seed)}`);
      if (!res.ok) return;
      const json: unknown = await res.json();
      const problem = proxyFileProblem(json, seed);
      if (problem) { console.warn(`[proxies] ignoring ${proxyFileFor(seed)}: ${problem}`); return; }
      this.file = json as ProxyFile;
      this.fileSeed = seed;
      this.stats.fileSites = this.file.sites.length;
      // The ladders are the assembly's column map — publish them the moment they are parsed
      // (a site that assembles before this lands keeps its single base→roof box).
      setProxyKeys(this.file.keys);
    } catch { /* absent or offline: far tier off */ } finally { this.loading = false; }
  }

  private tick(): void {
    if (++this.frame % TICK_EVERY !== 0) return;
    // Distant detail OFF (the graphics toggle) = the kill switch, live: the far ring and the silhouettes go.
    if (SettlementProxyModule.optedOut() || !distantDetailOn()) { if (this.boxMesh || this.hlod0.size) this.teardown(); return; }
    const inCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    if (!inCommons) { if (this.boxMesh || this.hlod0.size) this.teardown(); return; }
    if (!this.file) { void this.ensureFile(); return; }
    // The ladder (lib/settlement_lod.ts): HLOD-0 draws LOD2 inside the draw
    // distance and LOD3 out to 1.5× it, both on the EFFECTIVE distance (raw −
    // closing speed × 15 s) so a fast approach refines a city early.
    const preset = activePreset();
    const drawDist = currentDrawDistance();
    const bandsOf: Partial<Record<SettlementTier, SettlementBands>> = {};
    const cityBands = settlementBands('city', drawDist, preset.settlementRadiusScale, preset.settlementForceLowLod, preset.distantDetail !== false);
    this.stats.r0 = cityBands.hlod;
    this.stats.hlod2 = cityBands.hlod2;
    const px = atlas.localX, pz = atlas.localZ;
    const alt = Math.max(0, atlas.localY - 50);
    const vel = playerVelocity();
    const wantBoxes: ProxySite[] = [];
    const wantReal: Array<{ site: ProxySite; lod: 2 | 3 }> = [];
    for (const site of this.file.sites) {
      const dist = Math.hypot(site.cx - px, alt, site.cz - pz);
      const built = this.settlements.isBuilt(site.id);
      if (built) continue;
      const tier = site.tier as SettlementTier;
      const bands = bandsOf[tier] ?? (bandsOf[tier] = settlementBands(tier, drawDist, preset.settlementRadiusScale, preset.settlementForceLowLod, preset.distantDetail !== false));
      const eff = effectiveDistance(dist, closingSpeed(vel.vx, vel.vz, site.cx - px, site.cz - pz));
      const real = site.structs.length > 0 && proxyVisible(eff, false, this.hlod0.has(site.id), bands.hlod);
      if (real) wantReal.push({ site, lod: hlodLodFor(eff, bands) });
      else if (proxyVisible(eff, false, this.shown1.has(site.id), bands.boxes)) wantBoxes.push(site); // HLOD-1: bounded (2× draw distance, 4–12 km)
    }
    this.syncHlod0(wantReal);
    const key = wantBoxes.map((s) => s.id).join(',');
    if (key !== this.boxKey) { this.boxKey = key; this.rebuildBoxes(wantBoxes); }
    // Night (2026-10-06, the city-lights wave): the silhouettes go DARK — the light carpet (CityNightLightsModule)
    // carries a far town's streets and lit windows as points. WHY: the old uniform 0.35 × blue-grey glow lit every far
    // town as one flat blob of the same colour. With the carpet opted out the old glow stays as the fallback.
    if (this.boxMat) {
      const night = (uNightFactor.value as number) ?? 0;
      this.boxMat.emissiveIntensity = cityLightsCarryFarTier() ? 0 : 0.35 * night;
    }
  }

  // ── HLOD-0: real prefabs ───────────────────────────────────────────────────

  private ensureInstancer(): StaticInstancer {
    if (!this.instancer) this.instancer = new StaticInstancer(this.scene!, 4096, 'settlement-hlod0');
    return this.instancer;
  }

  /** Load (once per key and level) a key's simplified prefab: the LOD2 or LOD3 variant (pack-served), nearest-lower if absent. */
  private master(keyIdx: number, lod: 2 | 3): Promise<Master | null> {
    const k = this.file!.keys[keyIdx];
    const mk = `${k.key}#${lod}`;
    let p = this.masters.get(mk);
    if (!p) {
      p = (async () => {
        try {
          const { object, release } = await this.assets.loadRepresentation(BigInt(k.assetId), lod);
          object.updateMatrixWorld(true);
          const parts: Part[] = [];
          object.traverse((o) => {
            const m = o as THREE.Mesh;
            if (m.isMesh && m.geometry) parts.push({ geometry: m.geometry, material: Array.isArray(m.material) ? m.material[0] : m.material, local: m.matrixWorld.clone() });
          });
          // The SAME measure the assembly seats by (bboxFor → setFromObject):
          // the cooked civ GLBs are off-pivot by up to 1.3 km, so the seat
          // must read the box of THIS mesh, never trust the origin.
          const box = new THREE.Box3().setFromObject(object);
          const bb = { minX: box.min.x, maxX: box.max.x, minY: box.min.y, minZ: box.min.z, maxZ: box.max.z };
          this.stats.masters++;
          return { object, release, parts, bb };
        } catch { return null; }
      })();
      this.masters.set(mk, p);
    }
    return p;
  }

  private syncHlod0(want: Array<{ site: ProxySite; lod: 2 | 3 }>): void {
    const wantIds = new Set(want.map((w) => w.site.id));
    for (const [id, h] of this.hlod0) {
      if (!wantIds.has(id)) {
        for (const hd of h.handles) this.instancer?.remove(hd); for (const hd of h.prev) this.instancer?.remove(hd);
        this.hlod0.delete(id);
        this.stats.hlod0Solids -= h.solids;
        clearStructureSolids(hlod0SolidSite(id)); // the site left the ring, or its real set built (which registers its own)
      }
    }
    for (const { site, lod } of want) {
      const cur = this.hlod0.get(site.id);
      if (cur && (cur.lod === lod || cur.placing)) continue; // right level, or a swap already in flight
      // New site, or a band swap: the previous level's instances stay until
      // the new level has landed (removed at the end of placeHlod0).
      const entry: Hlod0Site = { site, lod, handles: [], placing: true, prev: cur ? [...cur.handles, ...cur.prev] : [], solids: cur?.solids ?? 0 };
      if (cur) this.stats.swaps++;
      // UNBUILT SITES COLLIDE TOO (2026-09-23): a ship flew through the far ring's real prefabs. The band
      // solids need only the ladder + the placement (no mesh), so they register the tick the site enters
      // HLOD-0 — synchronously, before its instances have loaded — and a band swap keeps them.
      else entry.solids = this.registerHlod0Solids(site);
      this.hlod0.set(site.id, entry);
      void this.placeHlod0(entry);
    }
    this.stats.hlod0Sites = this.hlod0.size;
    let n = 0, l2 = 0, l3 = 0;
    for (const h of this.hlod0.values()) { n += h.handles.length; if (h.lod === 2) l2++; else l3++; }
    this.stats.hlod0Instances = n;
    this.stats.lod2Sites = l2;
    this.stats.lod3Sites = l3;
  }

  /** Register an HLOD-0 site's placements as band solids (the assembly's cut, from the ladder alone). */
  private registerHlod0Solids(site: ProxySite): number {
    const sid = hlod0SolidSite(site.id);
    clearStructureSolids(sid);
    const seed = this.fileSeed;
    let n = 0;
    for (let i = 0; i + STRUCT_STRIDE <= site.structs.length; i += STRUCT_STRIDE) {
      const k = this.file!.keys[site.structs[i]];
      if (!k) continue;
      const wx = site.cx + site.structs[i + 1];
      const wz = site.cz + site.structs[i + 2];
      const yaw = site.structs[i + 3];
      const s = site.structs[i + 4];
      if (k.h * s < SOLID_MIN_H) continue;
      const gy = Number.isFinite(site.structs[i + 5]) ? site.structs[i + 5] : stampedHeight(seed, wx, wz);
      for (const so of ladderSolids(k.ladder, wx, wz, gy, yaw, s)) { addStructureSolid(sid, so); n++; }
    }
    this.stats.hlod0Solids += n;
    return n;
  }

  private async placeHlod0(entry: Hlod0Site): Promise<void> {
    const { site } = entry;
    const seed = this.fileSeed;
    const byKey = new Map<number, number[]>(); // keyIdx → struct offsets
    for (let i = 0; i + STRUCT_STRIDE <= site.structs.length; i += STRUCT_STRIDE) {
      const ki = site.structs[i];
      (byKey.get(ki) ?? byKey.set(ki, []).get(ki)!).push(i);
    }
    const inst = this.ensureInstancer();
    const place = new THREE.Matrix4();
    const world = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);
    const probe = new THREE.Vector3();
    await Promise.all([...byKey].map(async ([ki, offsets]) => {
      const m = await this.master(ki, entry.lod);
      if (!m || !this.hlod0.has(site.id) || this.hlod0.get(site.id) !== entry) return; // dropped while loading
      const key = this.file!.keys[ki].key;
      for (const i of offsets) {
        const wx = site.cx + site.structs[i + 1];
        const wz = site.cz + site.structs[i + 2];
        const yaw = site.structs[i + 3];
        const s = site.structs[i + 4];
        // Baked seat ground (the pad's flattened height, as the assembly
        // seats it); a v3 file always carries it, else fall back to the stamp.
        const gy = Number.isFinite(site.structs[i + 5]) ? site.structs[i + 5] : stampedHeight(seed, wx, wz);
        // Pivot compensation — the bbox bottom-centre lands on (wx, gy, wz).
        const [px, py, pz] = seatPlacement(wx, gy, wz, yaw, s, m.bb);
        pos.set(px, py, pz);
        quat.setFromAxisAngle(up, yaw);
        scl.set(s, s, s);
        place.compose(pos, quat, scl);
        for (const part of m.parts) {
          world.multiplyMatrices(place, part.local);
          entry.handles.push(inst.add(part.geometry, part.material, world));
        }
        // Audit: where did the mesh's bbox centre actually land, and did its
        // bottom seat on gy? (Proves the seat in-browser; costs one point.)
        probe.set((m.bb.minX + m.bb.maxX) / 2, m.bb.minY, (m.bb.minZ + m.bb.maxZ) / 2).applyMatrix4(place);
        const spread = Math.hypot(probe.x - site.cx, probe.z - site.cz);
        if (spread > this.audit.maxSpread) { this.audit.maxSpread = spread; this.audit.maxSpreadSite = site.id; this.audit.maxSpreadKey = key; }
        const err = probe.y - gy;
        if (this.audit.audited === 0 || err < this.audit.seatErrMin) this.audit.seatErrMin = err;
        if (this.audit.audited === 0 || err > this.audit.seatErrMax) this.audit.seatErrMax = err;
        this.audit.audited++;
      }
    }));
    // The new level has landed: retire the previous level's instances (a band
    // swap never shows a gap), and drop the site's boxes.
    entry.placing = false;
    if (this.hlod0.get(site.id) === entry) { for (const hd of entry.prev) inst.remove(hd); entry.prev = []; }
    this.boxKey = '';
  }

  // ── HLOD-1: silhouette boxes ───────────────────────────────────────────────

  private ensureBoxMesh(): THREE.InstancedMesh {
    if (this.boxMesh) return this.boxMesh;
    const geo = new THREE.BoxGeometry(1, 1, 1);
    geo.translate(0, 0.5, 0); // base-origin: y = band bottom, height along +y
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0.15, emissive: new THREE.Color(0.55, 0.6, 0.7), emissiveIntensity: 0 });
    pinStaticMotionVectors(mat); // 65k static boxes: zero object velocity (three's frozen previous instance matrices)
    const mesh = new THREE.InstancedMesh(geo, mat, BOX_CAPACITY);
    mesh.name = 'inst:settlement-proxies';
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.count = 0;
    this.anchor = makeAnchorGroup('settlement-proxies');
    this.anchor.group.add(mesh);
    this.scene?.add(this.anchor.group);
    this.unsubAnchor = onRenderAnchor(() => this.rewriteAnchorLocal());
    this.boxMesh = mesh;
    this.boxMat = mat;
    return mesh;
  }

  private rewriteAnchorLocal(): void {
    if (!this.boxMesh) return;
    const tmp = new THREE.Matrix4();
    for (let i = 0; i < this.boxMesh.count; i++) this.boxMesh.setMatrixAt(i, toAnchorLocal(this.absMatrices[i], tmp));
    this.boxMesh.instanceMatrix.needsUpdate = true;
  }

  private rebuildBoxes(sites: ProxySite[]): void {
    const mesh = this.ensureBoxMesh();
    const seed = this.fileSeed;
    const palette = this.file!.palette;
    const abs = new THREE.Matrix4();
    const local = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);
    const color = new THREE.Color();
    let n = 0;
    // The absolute matrices are a POOL reused across rebuilds (readers never look past mesh.count): a rebuild —
    // any hysteresis crossing near a dense cluster — cloned a fresh Matrix4 for every box, up to 65 536 of them,
    // several times a second while flying past a capital's districts (performance.md §18.6, 2026-09-22).
    this.shown1.clear();
    for (const site of sites) {
      const b = site.boxes;
      const gy0 = stampedHeight(seed, site.cx, site.cz); // fallback ground for a box without a baked seat
      for (let i = 0; i + BOX_STRIDE <= b.length && n < BOX_CAPACITY; i += BOX_STRIDE) {
        const wx = site.cx + b[i];
        const wz = site.cz + b[i + 1];
        // Each box seats on ITS building's baked pad height (the flattened
        // pad the assembly seats on), so a hillside city's skyline steps with
        // its terraces instead of floating off one site-centre sample.
        const gy = Number.isFinite(b[i + 8]) ? b[i + 8] : gy0;
        pos.set(wx, gy + b[i + 4], wz);
        quat.setFromAxisAngle(up, b[i + 6]);
        scl.set(b[i + 2], b[i + 5], b[i + 3]);
        abs.compose(pos, quat, scl);
        const slot = this.absMatrices[n];
        if (slot) slot.copy(abs); else this.absMatrices[n] = abs.clone();
        mesh.setMatrixAt(n, toAnchorLocal(abs, local));
        const c = palette[b[i + 7]] ?? palette[0];
        // Height shade: base 0.78 → crown 1.08 of the palette colour (v4 tint).
        const t = Number.isFinite(b[i + 9]) ? b[i + 9] : 0.5;
        const k = 0.78 + 0.3 * t;
        color.setRGB(Math.min(1, c[0] * k), Math.min(1, c[1] * k), Math.min(1, c[2] * k));
        mesh.setColorAt(n, color);
        n++;
      }
      this.shown1.add(site.id);
    }
    mesh.count = n;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    this.stats.boxSites = sites.length;
    this.stats.boxes = n;
    this.stats.rebuilds++;
  }

  private teardown(): void {
    for (const [id, h] of this.hlod0) { for (const hd of h.handles) this.instancer?.remove(hd); for (const hd of h.prev) this.instancer?.remove(hd); clearStructureSolids(hlod0SolidSite(id)); }
    this.hlod0.clear();
    this.stats.hlod0Solids = 0;
    if (this.unsubAnchor) { this.unsubAnchor(); this.unsubAnchor = null; }
    if (this.boxMesh) {
      this.anchor?.group.remove(this.boxMesh);
      this.boxMesh.geometry.dispose();
      (this.boxMesh.material as THREE.Material).dispose();
      this.boxMesh.dispose();
      this.boxMesh = null;
      this.boxMat = null;
    }
    if (this.anchor) { this.scene?.remove(this.anchor.group); this.anchor.release(); this.anchor = null; }
    this.absMatrices.length = 0;
    this.shown1.clear();
    this.boxKey = '';
    this.stats.boxSites = 0; this.stats.boxes = 0; this.stats.hlod0Sites = 0; this.stats.hlod0Instances = 0;
  }

  /** Diag for probes. */
  diag(): { enabled: boolean; fileSites: number; boxSites: number; boxes: number; hlod0Sites: number; hlod0Instances: number; hlod0Solids: number; r0: number; hlod2: number; lod2Sites: number; lod3Sites: number; masters: number; rebuilds: number; swaps: number; hlod0: number[]; hlod0Lods: Array<[number, number]>; audit: PlacementAudit; hlod0Radius: Array<[number, number]>; motion: { vx: number; vz: number; speed: number } } {
    return { enabled: !SettlementProxyModule.optedOut() && distantDetailOn(), ...this.stats, hlod0: [...this.hlod0.keys()], hlod0Lods: [...this.hlod0.values()].map((h) => [h.site.id, h.lod]), audit: { ...this.audit }, hlod0Radius: [...this.hlod0.values()].map((h) => [h.site.id, h.site.r]), motion: playerVelocity() };
  }

  dispose(): void {
    this.teardown();
    for (const p of this.masters.values()) void p.then((m) => m?.release());
    this.masters.clear();
    this.file = null;
  }
}
